// src/components/Users/Forms/VerifyEmail.jsx
// Landing page for the link in the confirmation email (/verify-email?token=...).
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { verifyEmail } from '../../../redux/slices/Users/authSlice';

const VerifyEmail = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState(token ? 'verifying' : 'invalid');
  // Tokens are single-use: without this guard StrictMode's double effect
  // would send a second request that fails after the first one succeeded.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    dispatch(verifyEmail(token))
      .unwrap()
      .then(() => {
        setStatus('success');
        setTimeout(() => navigate('/profile', { replace: true }), 1500);
      })
      .catch(() => setStatus('invalid'));
  }, [token, dispatch, navigate]);

  return (
    <>
      <Helmet>
        <title>{t('verifyEmail.title')}</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-teal-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6 bg-white p-8 rounded-2xl shadow-xl text-center">
          <h2 className="text-3xl font-extrabold text-gray-900">{t('verifyEmail.title')}</h2>

          {status === 'verifying' && (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-t-2 border-red-600 rounded-full animate-spin" />
              <p className="text-gray-700">{t('verifyEmail.verifying')}</p>
            </div>
          )}

          {status === 'success' && (
            <p className="text-green-700 font-medium">{t('verifyEmail.success')}</p>
          )}

          {status === 'invalid' && (
            <>
              <p className="text-red-700">{t('verifyEmail.invalid')}</p>
              <Link
                to="/login"
                className="inline-block w-full py-2 px-4 rounded-lg text-sm font-medium text-white bg-red-600 hover:bg-red-700"
              >
                {t('verifyEmail.requestNew')}
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifyEmail;
